const TIMEOUT_MS = 10 * 1000 // 10 seconds

const PREFIX = '[Audio.getDurationSeconds]'

export interface GetAudioDurationOptions {
  throwError?: boolean
}

/**
 * Get length of audio file in seconds. Supports Wav and Mp3, but not Webm.
 * Only usable in the browser. Doesn't have a limit on file size.
 * Has a timeout of 10 seconds, in case of corrupt files. It will resolve to 0 in that case.
 */
const getDurationSeconds = async (
  file: File,
  { throwError }: GetAudioDurationOptions = {}
): Promise<number> =>
  new Promise((resolve, reject) => {
    if (typeof window === 'undefined')
      return reject(new Error('getDurationSeconds is only usable in the browser'))

    const objectUrl = URL.createObjectURL(file)

    const audio = new Audio()

    const cleanup = () => {
      audio.src = ''
      URL.revokeObjectURL(objectUrl)
    }

    const timeout = setTimeout(() => {
      cleanup()
      console.error(PREFIX, 'Timeout', file.name, file.size, file.type)
      resolve(0)
    }, TIMEOUT_MS)

    audio.addEventListener(
      'loadedmetadata',
      () => {
        const duration = audio.duration
        clearTimeout(timeout)
        cleanup()
        resolve(duration)
      },
      { once: true }
    )

    audio.addEventListener(
      'error',
      (err) => {
        clearTimeout(timeout)
        cleanup()
        if (throwError) {
          console.error(PREFIX, 'Error', file.name, file.size, file.type, err)
          reject(file)
        } else {
          resolve(0)
        }
      },
      { once: true }
    )

    audio.src = objectUrl
  })

const _Audio = { getDurationSeconds }

export { _Audio as Audio }
